"use client";

import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import HeaderStrip from "./components/HeaderStrip";
import HeroSection from "./components/HeroSection";
import FeaturesSection from "./components/FeaturesSection";
import KnownLimitationsSection from "./components/KnownLimitationsSection";
import ContactAssistanceSection from "./components/ContactAssistanceSection";
import FeedbackSection from "./components/FeedbackSection";
import EnforcementSection from "./components/EnforcementSection";
import StandardsReferenceSection from "./components/StandardsReferenceSection";
import { injectMetaTags } from "@/lib/seoHelpers";

export default function AccessibilityPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();
  const isEs = currentLang === "es";

  useEffect(() => {
    const title = isEs
      ? "Declaraci\u00F3n de Accesibilidad \u2014 Windows and Doors Repair LLC Miami"
      : "Accessibility Statement \u2014 Windows and Doors Repair LLC Miami";
    document.title = title;

    let metaDesc = document.querySelector(
      'meta[name="description"]'
    ) as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = isEs
      ? "Declaraci\u00F3n de accesibilidad del sitio web de Windows and Doors Repair LLC. Conformidad WCAG 2.1 Nivel AA. Contacto 786.377.4452 para asistencia de accesibilidad."
      : "Accessibility statement for Windows and Doors Repair LLC website. WCAG 2.1 Level AA conformance. Contact 786.377.4452 for accessibility assistance.";

    const cleanupSeo = injectMetaTags({
      title,
      description: metaDesc.content,
      url: "https://impact-window-repair-miami.com/accessibility/",
      image: "https://impact-window-repair-miami.com/og/og-default.jpg",
      imageAlt: isEs
        ? "Declaraci\u00F3n de accesibilidad Windows and Doors Repair LLC"
        : "Accessibility statement Windows and Doors Repair LLC",
      breadcrumbs: [
        { name: isEs ? "Inicio" : "Home", item: "https://impact-window-repair-miami.com/" },
        { name: isEs ? "Accesibilidad" : "Accessibility", item: "https://impact-window-repair-miami.com/accessibility/" }
      ],
      type: "website",
    });

    const inLanguage = isEs ? "es-US" : "en-US";
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LocalBusiness",
          name: "Windows and Doors Repair LLC",
          telephone: "+17863774452",
          email: "contact@impact-window-repair-miami.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "848 Brickell Ave, PH-5",
            addressLocality: "Miami",
            addressRegion: "FL",
            postalCode: "33131",
            addressCountry: "US",
          },
          url: "https://impact-window-repair-miami.com/accessibility/",
          geo: {
            "@type": "GeoCoordinates",
            "latitude": 25.7617,
            "longitude": -80.1918
          },
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "07:00",
            "closes": "21:00"
          }],
          priceRange: "$$",
          paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
          currenciesAccepted: "USD",
          areaServed: [
            {"@type": "City", "name": "Miami"},
            {"@type": "AdministrativeArea", "name": "Miami-Dade County"},
            "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach"
          ],
          knowsLanguage: ["en", "es"],
          knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
          slogan: isEs
            ? "Reparaci\u00F3n Honesta de Ventanas Miami. Mismo D\u00EDa. Garant\u00EDa 30 D\u00EDas."
            : "Honest Miami Window Repair. Same-Day. 30-Day Guarantee."
        },
        {
          "@type": "WebPage",
          name: isEs ? "Declaraci\u00F3n de Accesibilidad" : "Accessibility Statement",
          url: "https://impact-window-repair-miami.com/accessibility/",
          isAccessibleForFree: true,
          inLanguage,
          accessibilityFeature: [
            "alternativeText",
            "highContrastDisplay",
            "largePrint",
            "structuralNavigation",
            "taggedPDF",
          ],
          accessibilityHazard: ["none"],
          accessibilityAPI: ["ARIA"],
          accessibilityControl: [
            "fullKeyboardControl",
            "fullMouseControl",
            "fullTouchControl",
          ],
          accessibilitySummary: isEs
            ? "El sitio web de Windows and Doors Repair LLC es parcialmente conforme con est\u00E1ndares WCAG 2.1 Nivel AA. La mayor\u00EDa de p\u00E1ginas son totalmente accesibles; algunos contenidos de terceros incrustados y contenido din\u00E1mico pueden tener limitaciones."
            : "Windows and Doors Repair LLC's website is partially conformant with WCAG 2.1 Level AA standards. Most pages are fully accessible; some third-party embeds and dynamic content may have limitations.",
        },
      ],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      cleanupSeo();
      if (metaDesc && metaDesc.parentNode)
        metaDesc.parentNode.removeChild(metaDesc);
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [currentLang]);

  return (
    <div className="font-roboto">
      <HeaderStrip />
      <HeroSection />

      {/* Commitment & Conformance */}
      <section className="bg-white py-12 md:py-16 px-4 md:px-6">
        <div className="max-w-[880px] mx-auto">
          <h2 className="text-xl md:text-[28px] font-black text-[#064e3b] mb-4">
            {t("access.h2Commitment")}
          </h2>
          <p className="text-[#374151] leading-[1.7] mb-10">
            {t("access.commitmentText")}
          </p>

          <h2 className="text-xl md:text-[28px] font-black text-[#064e3b] mb-4">
            {t("access.h2Conformance")}
          </h2>
          <p className="text-[#374151] leading-[1.7] mb-4">
            {t("access.conformanceText")}
          </p>
        </div>
      </section>

      <FeaturesSection />
      <KnownLimitationsSection />
      <ContactAssistanceSection />
      <FeedbackSection />
      <EnforcementSection />
      <StandardsReferenceSection />
    </div>
  );
}